/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { CheckInCircle, CheckInCircleSolid, Info, Launch } from "@gaming/xds-react-js-icons";

import { partnerCenterLegalInfoUrl, xboxOnboardSiteLocationPrefix } from "../../../../constants";
import { GameConceptProgress } from "../../../../helpers/gameConceptProgress";
import {
  formatGameConceptSummaryDate,
  getGameConceptSummaryData,
} from "../../../../helpers/gameConceptSummary";
import { useGenresList } from "../../../../helpers/optionSets";
import { useString, useStrings } from "../../../../localization/localizationHelpers";
import { CrmGameConceptStatus } from "../../../../models/crmStatus";
import { Programs } from "../../../../models/programsConfig";
import { GameConceptPageData } from "../../../../redux/actions/gameConceptPageActions";
import * as styles from "../../GameConceptPage.module.css";

type GameConceptSidePanelProps = Readonly<{
  accountType: GameConceptPageData["accountType"];
  conceptState: GameConceptPageData["state"];
  bigId?: string;
  formData: Record<string, Record<string, unknown>> | null | undefined;
  gameTitle: string;
  isReadOnly: boolean;
  isNewConcept: boolean;
  isOnboardingApproved: boolean;
  canSubmitIdApplication: boolean;
  showSubmissionProgress: boolean;
  allSectionsValid: boolean;
  isActiveSeller: boolean;
  hasPassedVetting: boolean;
  isSellerDetailsLoading: boolean;
  sellerDetailsError: boolean;
  onRetrySellerDetails: () => void;
  conceptProgress: GameConceptProgress;
  tlaReceivedDate: string | undefined;
  onDeleteRequest: () => void;
}>;

export const GameConceptSidePanel = ({
  accountType,
  conceptState,
  bigId,
  formData,
  gameTitle,
  isReadOnly,
  isNewConcept,
  isOnboardingApproved,
  canSubmitIdApplication,
  showSubmissionProgress,
  allSectionsValid,
  isActiveSeller,
  hasPassedVetting,
  isSellerDetailsLoading,
  sellerDetailsError,
  onRetrySellerDetails,
  conceptProgress,
  tlaReceivedDate,
  onDeleteRequest,
}: GameConceptSidePanelProps) => {
  const [
    submittedPrefix,
    moreOptionsAriaLabel,
    deleteButton,
    submissionRequirements,
    joinIdXboxRequirement,
    idXboxProgramName,
    completeRequirement,
    progressLabel,
    summaryTitle,
    summaryPlayableOn,
    summaryPrimaryGenre,
    summaryReleaseDate,
    summaryWebsite,
    otherLabel,
    activeSellerRequirement,
    vettingRequirement,
    sellerCheckingStatus,
    sellerRequirementsError,
    tryAgainLabel,
  ] = useStrings(
    "submitted",
    "moreOptionsAriaLabel",
    "deleteButton",
    "gameConceptSubmissionRequirements",
    "gameConceptJoinIdXboxRequirement",
    "gameConceptIdXboxProgramName",
    "gameConceptCompleteRequirement",
    "gameConceptProgressLabel",
    "gameConceptSummaryTitle",
    "gameConceptSummaryPlayableOn",
    "gameConceptSummaryPrimaryGenre",
    "gameConceptSummaryReleaseDate",
    "gameConceptSummaryWebsite",
    "other",
    "gameConceptActiveSellerRequirement",
    "gameConceptVettingRequirement",
    "gameConceptSellerCheckingStatus",
    "gameConceptSellerRequirementsError",
    "partnerAdminTryAgainLabel"
  );

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showMoreMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowMoreMenu(false);
        moreMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showMoreMenu]);

  const showSubmittedDate =
    conceptState?.status === CrmGameConceptStatus.UnderReview ||
    conceptState?.status === CrmGameConceptStatus.AdditionalContractsSent;
  const formattedSubmittedDate =
    showSubmittedDate && conceptState?.submittedDate
      ? new Date(conceptState.submittedDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : undefined;
  const formattedTlaReceivedDate =
    conceptState?.status === CrmGameConceptStatus.Accepted && tlaReceivedDate
      ? new Date(tlaReceivedDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })
      : undefined;
  const tlaSignedDateLabel = useString(
    "gameConceptTlaSignedOnDate",
    formattedTlaReceivedDate ?? ""
  );
  const administratorGuidance = useString("gameConceptIdXboxAdministratorRequired");
  const storeIdLabel = useString("gameConceptBigIdLabel");
  const submittedStoreId =
    conceptState && conceptState.status !== CrmGameConceptStatus.Unset ? bigId?.trim() : undefined;

  const genres = useGenresList();
  const summaryData = useMemo(() => getGameConceptSummaryData(formData), [formData]);
  const genreLabel = genres.find(option => String(option.value) === summaryData.genreCode)?.label;
  const primaryGenre =
    genreLabel === otherLabel && summaryData.otherGenre ? summaryData.otherGenre : genreLabel;
  const releaseDate = formatGameConceptSummaryDate(summaryData.releaseDate);
  const hasSummary =
    Boolean(summaryData.description) ||
    summaryData.playableOn.length > 0 ||
    Boolean(primaryGenre) ||
    Boolean(releaseDate) ||
    Boolean(summaryData.website);

  return (
    <aside className={styles.sidePanel}>
      <div className={styles.gameInfoCard}>
        <div className={styles.gameInfoHeader}>
          <h3 className={styles.gameInfoTitle}>{gameTitle}</h3>
          {!isReadOnly && !isNewConcept && (
            <div className={styles.moreMenuWrapper} ref={moreMenuRef}>
              <button
                ref={moreMenuButtonRef}
                type="button"
                className={styles.moreButton}
                onClick={() => setShowMoreMenu(previous => !previous)}
                aria-label={moreOptionsAriaLabel}
                aria-haspopup="menu"
                aria-expanded={showMoreMenu}
                aria-controls={showMoreMenu ? "game-concept-more-menu" : undefined}
              >
                ···
              </button>
              {showMoreMenu && (
                <div
                  id="game-concept-more-menu"
                  className={styles.moreMenu}
                  role="menu"
                  tabIndex={-1}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.moreMenuItem}
                    onClick={() => {
                      setShowMoreMenu(false);
                      onDeleteRequest();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path
                        d="M6.5 2h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5ZM5 3v-.5A1.5 1.5 0 0 1 6.5 1h3A1.5 1.5 0 0 1 11 2.5V3h3.5a.5.5 0 0 1 0 1H13.9l-.6 8.5A1.5 1.5 0 0 1 11.8 14H4.2a1.5 1.5 0 0 1-1.5-1.5L2.1 4H1.5a.5.5 0 0 1 0-1H5Zm1.5 3.5a.5.5 0 0 0-1 0v5a.5.5 0 0 0 1 0v-5Zm3 0a.5.5 0 0 0-1 0v5a.5.5 0 0 0 1 0v-5Z"
                        fill="#e07070"
                      />
                    </svg>
                    {deleteButton}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {formattedSubmittedDate && (
          <div className={styles.gameInfoSubmittedDate}>
            <Icon
              aria-hidden="true"
              className={styles.gameInfoSubmittedDateIcon}
              icon={CheckInCircleSolid}
              iconSize={IconSize.Custom}
            />
            <span>
              {submittedPrefix} {formattedSubmittedDate}
            </span>
          </div>
        )}
        {formattedTlaReceivedDate && (
          <div className={styles.gameInfoSubmittedDate}>
            <Icon
              aria-hidden="true"
              className={styles.gameInfoSubmittedDateIcon}
              icon={CheckInCircleSolid}
              iconSize={IconSize.Custom}
            />
            <span>{tlaSignedDateLabel}</span>
          </div>
        )}
        {showSubmissionProgress && (
          <>
            <div className={styles.submissionRequirements}>
              <h4 className={styles.submissionRequirementsTitle}>{submissionRequirements}</h4>
              {accountType !== Programs.Aaa && (
                <>
                  <div
                    className={`${styles.requirementRow} ${
                      canSubmitIdApplication ? "" : styles.requirementRowDisabled
                    }`}
                  >
                    <Icon
                      aria-hidden="true"
                      className={`${styles.requirementIcon} ${
                        isOnboardingApproved ? styles.requirementIconComplete : ""
                      }`}
                      icon={isOnboardingApproved ? CheckInCircleSolid : CheckInCircle}
                      iconSize={IconSize.Custom}
                    />
                    <span>{joinIdXboxRequirement}</span>
                    {canSubmitIdApplication ? (
                      <Link
                        className={styles.idXboxRequirementLink}
                        to={`${xboxOnboardSiteLocationPrefix}/id`}
                      >
                        {idXboxProgramName}
                        <Icon
                          aria-hidden="true"
                          className={styles.externalLinkIcon}
                          icon={Launch}
                          iconSize={IconSize.Custom}
                        />
                      </Link>
                    ) : (
                      <span
                        aria-disabled="true"
                        className={styles.idXboxRequirementLink}
                        role="link"
                      >
                        {idXboxProgramName}
                      </span>
                    )}
                  </div>
                  {!canSubmitIdApplication && !isOnboardingApproved && (
                    <div className={styles.requirementCallout} role="status">
                      <Icon
                        aria-hidden="true"
                        className={styles.requirementCalloutIcon}
                        icon={Info}
                        iconSize={IconSize.Custom}
                      />
                      <span>{administratorGuidance}</span>
                    </div>
                  )}
                </>
              )}
              <div className={styles.requirementRow}>
                <Icon
                  aria-hidden="true"
                  className={`${styles.requirementIcon} ${
                    allSectionsValid ? styles.requirementIconComplete : ""
                  }`}
                  icon={allSectionsValid ? CheckInCircleSolid : CheckInCircle}
                  iconSize={IconSize.Custom}
                />
                <span>{completeRequirement}</span>
              </div>
              {[
                { label: activeSellerRequirement, complete: isActiveSeller, href: undefined },
                {
                  label: vettingRequirement,
                  complete: hasPassedVetting,
                  href:
                    !hasPassedVetting && !isSellerDetailsLoading && !sellerDetailsError
                      ? partnerCenterLegalInfoUrl
                      : undefined,
                },
              ].map(requirement => (
                <div
                  className={styles.requirementRow}
                  key={requirement.label}
                  aria-busy={isSellerDetailsLoading}
                >
                  {isSellerDetailsLoading ? (
                    <span
                      aria-hidden="true"
                      className={`${styles.requirementIcon} ${styles.requirementSpinner}`}
                    />
                  ) : (
                    <Icon
                      aria-hidden="true"
                      className={`${styles.requirementIcon} ${
                        requirement.complete ? styles.requirementIconComplete : ""
                      }`}
                      icon={requirement.complete ? CheckInCircleSolid : CheckInCircle}
                      iconSize={IconSize.Custom}
                    />
                  )}
                  <span>{requirement.label}</span>
                  {requirement.href && (
                    <a
                      aria-label={requirement.label}
                      className={styles.idXboxRequirementLink}
                      href={requirement.href}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <Icon
                        aria-hidden="true"
                        className={styles.externalLinkIcon}
                        icon={Launch}
                        iconSize={IconSize.Custom}
                      />
                    </a>
                  )}
                </div>
              ))}
              {isSellerDetailsLoading && (
                <p className={styles.screenReaderStatus} role="status">
                  {sellerCheckingStatus}
                </p>
              )}
              {sellerDetailsError && (
                <div role="alert">
                  <p>{sellerRequirementsError}</p>
                  <button
                    type="button"
                    className={styles.saveButton}
                    onClick={onRetrySellerDetails}
                  >
                    {tryAgainLabel}
                  </button>
                </div>
              )}
            </div>
            <div className={styles.gameConceptProgress}>
              <div className={styles.gameConceptProgressText}>
                <span>{progressLabel}</span>
                <span>{conceptProgress.percentage}%</span>
              </div>
              <progress
                aria-label={`${progressLabel} ${conceptProgress.percentage}%`}
                className={styles.gameConceptProgressBar}
                max={conceptProgress.totalSections}
                value={conceptProgress.completedSections}
              />
            </div>
          </>
        )}
        {hasSummary && (
          <section className={styles.gameConceptSummary}>
            <h4 className={styles.gameConceptSummaryTitle}>{summaryTitle}</h4>
            {summaryData.description && (
              <p className={styles.gameConceptSummaryDescription}>{summaryData.description}</p>
            )}
            <dl className={styles.gameConceptSummaryList}>
              {summaryData.playableOn.length > 0 && (
                <div className={styles.gameConceptSummaryRow}>
                  <dt>{summaryPlayableOn}</dt>
                  <dd>{summaryData.playableOn.join(", ")}</dd>
                </div>
              )}
              {primaryGenre && (
                <div className={styles.gameConceptSummaryRow}>
                  <dt>{summaryPrimaryGenre}</dt>
                  <dd>{primaryGenre}</dd>
                </div>
              )}
              {releaseDate && (
                <div className={styles.gameConceptSummaryRow}>
                  <dt>{summaryReleaseDate}</dt>
                  <dd>{releaseDate}</dd>
                </div>
              )}
              {summaryData.website && (
                <div className={styles.gameConceptSummaryRow}>
                  <dt>{summaryWebsite}</dt>
                  <dd>
                    <a
                      className={styles.gameConceptSummaryLink}
                      href={summaryData.website.href}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {summaryData.website.label}
                      <Icon
                        aria-hidden="true"
                        className={styles.externalLinkIcon}
                        icon={Launch}
                        iconSize={IconSize.Custom}
                      />
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </section>
        )}
        {submittedStoreId && (
          <section
            className={styles.gameConceptSummary}
            aria-labelledby="game-concept-store-id-title"
          >
            <h4 id="game-concept-store-id-title" className={styles.gameConceptSummaryTitle}>
              {storeIdLabel}
            </h4>
            <p className={styles.gameConceptSummaryDescription}>{submittedStoreId}</p>
          </section>
        )}
      </div>
    </aside>
  );
};
